import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  confirmPerformance,
  getEventInstance,
  getPerformerStatus,
} from "../../services/firebase";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import PlaceIcon from "@mui/icons-material/Place";
import { useParams } from "react-router-dom";

const ConfirmPerformance = () => {
  const { performerId } = useParams();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [performerStatus, setPerformerStatus] = useState(null);
  const [eventInstance, setEventInstance] = useState(null);
  const [discountCode, setDiscountCode] = useState(null);

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "TBD";
    return date instanceof Date
      ? date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          weekday: "long",
        })
      : "Invalid date";
  };

  // Format time for display
  const formatTime = (date) => {
    if (!date) return "TBD";
    return date instanceof Date
      ? date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "Invalid time";
  };

  useEffect(() => {
    const fetchPerformerData = async () => {
      try {
        const status = await getPerformerStatus(performerId);
        setPerformerStatus(status);

        // If performer exists and has an event instance ID, fetch the event details
        if (status && status.exists && status.eventInstanceId) {
          const instance = await getEventInstance(status.eventInstanceId);
          setEventInstance(instance);
        }
      } catch (err) {
        setError("Unable to verify performer status. Please try again later.");
      }
    };

    fetchPerformerData();
  }, [performerId]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      const confirmation = await confirmPerformance(performerId);
      if (confirmation.discountCode) {
        setDiscountCode(confirmation.discountCode);
      }
      setIsConfirmed(true);
    } catch (err) {
      setError(
        err.message ||
          "An error occurred while confirming your performance. Please try again."
      );
    } finally {
      setIsConfirming(false);
    }
  };

  if (isConfirming || performerStatus === null) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (!performerStatus.exists) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h5" gutterBottom>
          Invalid Performer ID
        </Typography>
        <Typography variant="body1">
          The performer ID provided is not valid. Please check your email for
          the correct link.
        </Typography>
      </Box>
    );
  }

  if (!performerStatus.selected) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h5" gutterBottom>
          Not Selected for Performance
        </Typography>
        <Typography variant="body1">
          We're sorry, but you were not selected for this performance. Thank you
          for your interest.
        </Typography>
      </Box>
    );
  }

  if (performerStatus.confirmed) {
    return (
      <Box textAlign="center" mt={4}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
          <Typography variant="h5" gutterBottom>
            Performance Already Confirmed
          </Typography>
          <Typography variant="body1" paragraph>
            You have already confirmed your performance. We look forward to
            seeing you at the Open Mic Night.
          </Typography>

          {eventInstance && (
            <Box sx={{ mt: 4, textAlign: "left" }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Event Details
              </Typography>

              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "center", my: 1 }}
              >
                <EventIcon sx={{ mr: 1 }} />
                {formatDate(eventInstance.startDate)}
              </Typography>

              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "center", my: 1 }}
              >
                <AccessTimeIcon sx={{ mr: 1 }} />
                {formatTime(eventInstance.startDate)}
              </Typography>

              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "center", my: 1 }}
              >
                <PlaceIcon sx={{ mr: 1 }} />
                The Cola, 2834 Cherokee St. St. Louis, MO 63118
              </Typography>
            </Box>
          )}

          {discountCode && (
            <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body1" gutterBottom>
                Want to bring a friend for free? Please share this discount code{" "}
                <strong>{discountCode}</strong> for a complimentary ticket for
                one guest. Thank you! This discount can only be used once and is
                valid on the day of the event.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  if (isConfirmed) {
    return (
      <Box textAlign="center" mt={4}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
          <Typography variant="h5" gutterBottom>
            Your performance has been confirmed. Thank you!
          </Typography>
          <Typography variant="body1" paragraph>
            We look forward to seeing you at the Open Mic Night.
          </Typography>

          {eventInstance && (
            <Box sx={{ mt: 4, textAlign: "left" }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Event Details
              </Typography>

              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "center", my: 1 }}
              >
                <EventIcon sx={{ mr: 1 }} />
                {formatDate(eventInstance.startDate)}
              </Typography>

              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "center", my: 1 }}
              >
                <AccessTimeIcon sx={{ mr: 1 }} />
                {formatTime(eventInstance.startDate)}
              </Typography>

              <Typography
                variant="body1"
                sx={{ display: "flex", alignItems: "center", my: 1 }}
              >
                <PlaceIcon sx={{ mr: 1 }} />
                The Cola, 2834 Cherokee St. St. Louis, MO 63118
              </Typography>
            </Box>
          )}

          {discountCode && (
            <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body1" gutterBottom>
                Want to bring a friend for free? Please share this discount code{" "}
                <strong>{discountCode}</strong> for a complimentary ticket for
                one guest. Thank you! This discount can only be used once and is
                valid on the day of the event.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box textAlign="center" mt={4}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
        <Typography variant="h5" gutterBottom>
          Confirm Your Performance
        </Typography>

        {eventInstance && (
          <Box sx={{ my: 3, textAlign: "left" }}>
            <Typography variant="h6" gutterBottom>
              Event Details
            </Typography>

            <Typography
              variant="body1"
              sx={{ display: "flex", alignItems: "center", my: 1 }}
            >
              <EventIcon sx={{ mr: 1 }} />
              {formatDate(eventInstance.startDate)}
            </Typography>

            <Typography
              variant="body1"
              sx={{ display: "flex", alignItems: "center", my: 1 }}
            >
              <AccessTimeIcon sx={{ mr: 1 }} />
              {formatTime(eventInstance.startDate)}
            </Typography>

            <Typography
              variant="body1"
              sx={{ display: "flex", alignItems: "center", my: 1 }}
            >
              <PlaceIcon sx={{ mr: 1 }} />
              The Cola, 2834 Cherokee St. St. Louis, MO 63118
            </Typography>
          </Box>
        )}

        <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
          Please click the button below to confirm your attendance for the Open
          Mic Night.
        </Typography>

        <Button variant="contained" onClick={handleConfirm} sx={{ mt: 2 }}>
          Confirm Performance
        </Button>
      </Paper>
    </Box>
  );
};

export default ConfirmPerformance;
